import { Link } from "react-router-dom";
import { ArrowRight, Fence, Users, CalendarDays } from "lucide-react";

const FACTS = [
  { icon: Fence, title: "Fully fenced", copy: "Secure space for suitable dogs, with individual assessment for every new face." },
  { icon: Users, title: "Considered groups", copy: "Dogs matched on temperament, energy and play style, never filled to capacity." },
  { icon: CalendarDays, title: "Mon · Tue · Fri", copy: "Regular walking days, with other fenced fields and forest trails for variety." },
];

export const Meadow = () => (
  <section className="relative bg-[#3F4A38] text-[#F7F4ED] py-16 sm:py-24 lg:py-32 overflow-hidden" data-testid="meadow-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <p className="eyebrow-light">The signature setting</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight mt-4" data-testid="meadow-heading">
            Mary's Meadow.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F7F4ED]/80">
            Set beneath Culloden Viaduct, Mary's Meadow is a 4.6-acre private field
            and the signature setting for many Mary Pawpins walks. There is room to
            run, explore and enjoy the countryside, with groups chosen to suit the
            dogs involved.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#F7F4ED]/60">
            Other fully fenced fields are used occasionally for a change of scenery,
            and selected smaller groups may enjoy forest trails where appropriate.
          </p>
          <Link
            to="/dog-walking"
            data-testid="meadow-discover-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#F7F4ED] text-[#292A26] px-7 py-3.5 text-sm font-semibold hover:bg-white transition-colors"
          >
            Discover Our Walks
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-12 gap-4">
            <img
              src="/images/marypawpins/two-doodles-playing.webp"
              alt="Two dogs leaping joyfully towards each other at Mary's Meadow"
              className="col-span-12 rounded-3xl w-full aspect-[16/9] object-cover object-[50%_45%] shadow-2xl"
              data-testid="meadow-image-main"
            />
            <img
              src="/images/marypawpins/lab-yarrow-dachshunds.webp"
              alt="A labrador resting among the wildflowers"
              className="col-span-6 rounded-3xl w-full aspect-[4/3] object-cover"
            />
            <video
              src="/videos/carly-field-run.mp4"
              poster="/videos/carly-field-run.jpg"
              autoPlay muted loop playsInline
              className="col-span-6 rounded-3xl w-full aspect-[4/3] object-cover object-[50%_88%]"
              data-testid="meadow-home-video"
            />
          </div>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {FACTS.map(({ icon: Icon, title, copy }) => (
          <div key={title} className="rounded-2xl border border-[#F7F4ED]/15 bg-[#5C6B51] p-6" data-testid={`meadow-fact-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Icon className="w-5 h-5 text-[#D8B7B0]" />
            <p className="mt-4 font-serif text-xl">{title}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#F7F4ED]/65">{copy}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
